import { call, put, takeLatest, select } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import { projectValidationSliceAction } from "../../slices/project-validation";
import {
  projectValidationSagaActionTypes,
  extractNumericId,
  createCategoryPayloadType,
  updateCategoryPayloadType,
  createRequirementPayloadType,
  updateRequirementPayloadType,
  createTestCasePayloadType,
  updateTestCasePayloadType,
  getCategoriesSagaAction,
  getRequirementsSagaAction,
  getTestCasesSagaAction,
  getMediaFilesSagaAction,
  deleteMediaFilePayloadType,
  getHistoryEnvironmentDetailsPayloadType,
} from "./saga-actions";
import {
  CategoryType,
  RequirementType,
  TestCaseType,
  GetMediaFilesResponse,
  UploadMediaFilesResponse,
  DeleteMediaFileResponse,
  EnvironmentDetails,
} from "../../slices/project-validation/Types";
import {
  deleteMediaAPI,
  getMediaAPI,
  uploadMediaAPI,
} from "../../../common/api/media";

/** Validation docs/history files are stored under the integration media type. */
const VALIDATION_MEDIA_TYPE = "integration";

// API Response Types
type getCategoriesAPIResponseType = {
  success: boolean;
  message?: string;
  data: CategoryType[];
};

type getCategoryAPIResponseType = {
  success: boolean;
  message?: string;
  data: CategoryType[];
};

type getRequirementsAPIResponseType = {
  success: boolean;
  message?: string;
  rows: RequirementType[];
};

type getRequirementAPIResponseType = {
  success: boolean;
  message?: string;
  requirement: RequirementType | RequirementType[];
};

type getTestCasesAPIResponseType = {
  success: boolean;
  message?: string;
  rows: TestCaseType[];
};

type getTestCaseAPIResponseType = {
  success: boolean;
  message?: string;
  rows: TestCaseType[];
};

type createAPIResponseType = {
  success: boolean;
  message: string;
};

type getEnvironmentDetailsAPIResponseType = {
  success: boolean;
  environment: EnvironmentDetails | null;
  source?: "current" | "history" | "none";
  used_document_id?: number;
};

// API Functions
const getCategoriesAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getCategoriesAPIResponseType>>(
      "/project/validation/category"
    )
    .then((res) => res.data);
  return response;
};

const getCategoryAPI = async (id: string | number) => {
  const numericId = extractNumericId(id);
  let response = axiosRestInstance
    .get<AxiosResponse<getCategoryAPIResponseType>>(
      `/project/validation/category/${numericId}`
    )
    .then((res) => res.data);
  return response;
};

const createCategoryAPI = async (data: createCategoryPayloadType) => {
  let response = axiosRestInstance
    .post<AxiosResponse<createAPIResponseType>>(
      "/project/validation/category",
      data
    )
    .then((res) => res.data);
  return response;
};

const updateCategoryAPI = async (data: updateCategoryPayloadType) => {
  const numericId = extractNumericId(data.id);
  let response = axiosRestInstance
    .put<AxiosResponse<createAPIResponseType>>(
      `/project/validation/category/${numericId}`,
      {
        category_name: data.category_name,
        description: data.description,
      }
    )
    .then((res) => res.data);
  return response;
};

const deleteCategoryAPI = async (id: string | number) => {
  const numericId = extractNumericId(id);
  let response = axiosRestInstance
    .delete<AxiosResponse<createAPIResponseType>>(
      `/project/validation/category/${numericId}`
    )
    .then((res) => res.data);
  return response;
};

const getRequirementsAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getRequirementsAPIResponseType>>(
      "/project/validation/requirement"
    )
    .then((res) => res.data);
  return response;
};

const getRequirementAPI = async (id: string | number) => {
  const numericId = extractNumericId(id);
  let response = axiosRestInstance
    .get<AxiosResponse<getRequirementAPIResponseType>>(
      `/project/validation/requirement/${numericId}`
    )
    .then((res) => res.data);
  return response;
};

const createRequirementAPI = async (data: createRequirementPayloadType) => {
  let response = axiosRestInstance
    .post<AxiosResponse<createAPIResponseType>>(
      "/project/validation/requirement",
      data
    )
    .then((res) => res.data);
  return response;
};

const updateRequirementAPI = async (data: updateRequirementPayloadType) => {
  const numericId = extractNumericId(data.id);
  let response = axiosRestInstance
    .put<AxiosResponse<createAPIResponseType>>(
      `/project/validation/requirement/${numericId}`,
      {
        category_id: data.category_id,
        requirement_type: data.requirement_type,
        technology: data.technology,
        integration_type: data.integration_type,
        test_cases: data.test_cases,
        validation_score: data.validation_score,
        requirement_status: data.requirement_status,
        high_level_scenario: data.high_level_scenario,
        requirement_description: data.requirement_description,
      }
    )
    .then((res) => res.data);
  return response;
};

const deleteRequirementAPI = async (id: string | number) => {
  const numericId = extractNumericId(id);
  let response = axiosRestInstance
    .delete<AxiosResponse<createAPIResponseType>>(
      `/project/validation/requirement/${numericId}`
    )
    .then((res) => res.data);
  return response;
};

const getTestCasesAPI = async () => {
  let response = axiosRestInstance
    .get<AxiosResponse<getTestCasesAPIResponseType>>(
      "/project/validation/test-case"
    )
    .then((res) => res.data);
  return response;
};

const getTestCaseAPI = async (id: string | number) => {
  const numericId = extractNumericId(id);
  let response = axiosRestInstance
    .get<AxiosResponse<getTestCaseAPIResponseType>>(
      `/project/validation/test-case/${numericId}`
    )
    .then((res) => res.data);
  return response;
};

const createTestCaseAPI = async (data: createTestCasePayloadType) => {
  let response = axiosRestInstance
    .post<AxiosResponse<createAPIResponseType>>(
      "/project/validation/test-case",
      data
    )
    .then((res) => res.data);
  return response;
};

const updateTestCaseAPI = async (data: updateTestCasePayloadType) => {
  const numericId = extractNumericId(data.id);
  let response = axiosRestInstance
    .put<AxiosResponse<createAPIResponseType>>(
      `/project/validation/test-case/${numericId}`,
      {
        category_id: data.category_id,
        high_level_scenario: data.high_level_scenario,
        pre_conditions: data.pre_conditions,
        description: data.description,
        expected_result: data.expected_result,
        test_case_status: data.test_case_status,
      }
    )
    .then((res) => res.data);
  return response;
};

const deleteTestCaseAPI = async (id: string | number) => {
  const numericId = extractNumericId(id);
  let response = axiosRestInstance
    .delete<AxiosResponse<createAPIResponseType>>(
      `/project/validation/test-case/${numericId}`
    )
    .then((res) => res.data);
  return response;
};

// Media Files API Functions — use shared global media helpers
const getMediaFilesAPI = async (integrationId: string) =>
  getMediaAPI(VALIDATION_MEDIA_TYPE, integrationId);

// TEMPORARY: created_at from validation \"Submitted on\" datepicker. Remove with TEMPORARY block.
const uploadMediaFilesAPI = async (
  integrationId: string,
  files: File[],
  uploadedBy: number,
  createdAt?: string
) =>
  uploadMediaAPI({
    type: VALIDATION_MEDIA_TYPE,
    typeId: integrationId,
    files,
    uploadedBy,
    createdAt,
  });

const deleteMediaFileAPI = async (mediaId: number) => deleteMediaAPI(mediaId);

const getEnvironmentDetailsAPI = async (params: {
  integration_id: number;
  documentation_id?: number;
}) => {
  const searchParams = new URLSearchParams();
  searchParams.append("integration_id", String(params.integration_id));
  if (params.documentation_id !== undefined && params.documentation_id !== null) {
    searchParams.append("documentation_id", String(params.documentation_id));
  }
  const response = await axiosRestInstance.get<
    AxiosResponse<getEnvironmentDetailsAPIResponseType>
  >(`/project/integration-validation/environment?${searchParams.toString()}`);
  return response.data;
};

// Saga Functions
function* getCategoriesSaga() {
  try {
    yield put(projectValidationSliceAction.setCategoriesLoading(true));
    let response: getCategoriesAPIResponseType = yield call(getCategoriesAPI);
    yield put(projectValidationSliceAction.setCategories(response.data || []));
    yield put(projectValidationSliceAction.setCategoriesLoading(false));
  } catch (e) {
    yield put(projectValidationSliceAction.setCategories([]));
    yield put(projectValidationSliceAction.setCategoriesLoading(false));
    console.log(e);
  }
}

function* getCategorySaga(action: {
  type: string;
  payload: { id: string | number };
}) {
  try {
    yield put(projectValidationSliceAction.setCategoryLoading(true));
    let response: getCategoryAPIResponseType = yield call(
      getCategoryAPI,
      action.payload.id
    );
    yield put(projectValidationSliceAction.setCategoryLoading(false));
  } catch (e) {
    yield put(projectValidationSliceAction.setCategoryLoading(false));
    console.log(e);
  }
}

function* createCategorySaga(action: {
  type: string;
  payload: createCategoryPayloadType;
}) {
  try {
    yield put(projectValidationSliceAction.setCreateCategoryLoading(true));
    let response: createAPIResponseType = yield call(
      createCategoryAPI,
      action.payload
    );
    yield put(projectValidationSliceAction.setCreateCategoryLoading(false));
    yield put(getCategoriesSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setCreateCategoryLoading(false));
    console.log(e);
  }
}

function* updateCategorySaga(action: {
  type: string;
  payload: updateCategoryPayloadType;
}) {
  try {
    yield put(projectValidationSliceAction.setUpdateCategoryLoading(true));
    let response: createAPIResponseType = yield call(
      updateCategoryAPI,
      action.payload
    );
    yield put(projectValidationSliceAction.setUpdateCategoryLoading(false));
    yield put(getCategoriesSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setUpdateCategoryLoading(false));
    console.log(e);
  }
}

function* deleteCategorySaga(action: {
  type: string;
  payload: { id: string | number };
}) {
  try {
    yield put(projectValidationSliceAction.setDeleteCategoryLoading(true));
    let response: createAPIResponseType = yield call(
      deleteCategoryAPI,
      action.payload.id
    );
    yield put(projectValidationSliceAction.setDeleteCategoryLoading(false));
    yield put(getCategoriesSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setDeleteCategoryLoading(false));
    console.log(e);
  }
}

function* getRequirementsSaga() {
  try {
    yield put(projectValidationSliceAction.setRequirementsLoading(true));
    const response: getRequirementsAPIResponseType = yield call(
      getRequirementsAPI
    );
    yield put(
      projectValidationSliceAction.setRequirements(response.rows || [])
    );
    yield put(projectValidationSliceAction.setRequirementsLoading(false));
  } catch (e) {
    yield put(projectValidationSliceAction.setRequirements([]));
    yield put(projectValidationSliceAction.setRequirementsLoading(false));
    console.log(e);
  }
}

function* getRequirementSaga(action: {
  type: string;
  payload: { id: string | number };
}) {
  try {
    yield put(projectValidationSliceAction.setRequirementLoading(true));
    const response: getRequirementAPIResponseType = yield call(
      getRequirementAPI,
      action.payload.id
    );
    yield put(projectValidationSliceAction.setRequirementLoading(false));
  } catch (e) {
    yield put(projectValidationSliceAction.setRequirementLoading(false));
    console.log(e);
  }
}

function* createRequirementSaga(action: {
  type: string;
  payload: createRequirementPayloadType;
}) {
  try {
    yield put(projectValidationSliceAction.setCreateRequirementLoading(true));
    let response: createAPIResponseType = yield call(
      createRequirementAPI,
      action.payload
    );
    yield put(projectValidationSliceAction.setCreateRequirementLoading(false));
    yield put(getRequirementsSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setCreateRequirementLoading(false));
    console.log(e);
  }
}

function* updateRequirementSaga(action: {
  type: string;
  payload: updateRequirementPayloadType;
}) {
  try {
    yield put(projectValidationSliceAction.setUpdateRequirementLoading(true));
    let response: createAPIResponseType = yield call(
      updateRequirementAPI,
      action.payload
    );
    yield put(projectValidationSliceAction.setUpdateRequirementLoading(false));
    yield put(getRequirementsSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setUpdateRequirementLoading(false));
    console.log(e);
  }
}

function* deleteRequirementSaga(action: {
  type: string;
  payload: { id: string | number };
}) {
  try {
    yield put(projectValidationSliceAction.setDeleteRequirementLoading(true));
    let response: createAPIResponseType = yield call(
      deleteRequirementAPI,
      action.payload.id
    );
    yield put(projectValidationSliceAction.setDeleteRequirementLoading(false));
    yield put(getRequirementsSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setDeleteRequirementLoading(false));
    console.log(e);
  }
}

function* getTestCasesSaga() {
  try {
    yield put(projectValidationSliceAction.setTestCasesLoading(true));
    const response: getTestCasesAPIResponseType = yield call(getTestCasesAPI);
    yield put(projectValidationSliceAction.setTestCases(response.rows || []));
    yield put(projectValidationSliceAction.setTestCasesLoading(false));
  } catch (e) {
    yield put(projectValidationSliceAction.setTestCases([]));
    yield put(projectValidationSliceAction.setTestCasesLoading(false));
    console.log(e);
  }
}

function* getTestCaseSaga(action: {
  type: string;
  payload: { id: string | number };
}) {
  try {
    yield put(projectValidationSliceAction.setTestCaseLoading(true));
    const response: getTestCaseAPIResponseType = yield call(
      getTestCaseAPI,
      action.payload.id
    );
    if (response.rows && response.rows.length > 0) {
      yield put(
        projectValidationSliceAction.setSelectedTestCase(response.rows[0])
      );
    }
    yield put(projectValidationSliceAction.setTestCaseLoading(false));
  } catch (e) {
    yield put(projectValidationSliceAction.setTestCaseLoading(false));
    console.error("Error fetching test case:", e);
  }
}

function* createTestCaseSaga(action: {
  type: string;
  payload: createTestCasePayloadType;
}) {
  try {
    yield put(projectValidationSliceAction.setCreateTestCaseLoading(true));
    let response: createAPIResponseType = yield call(
      createTestCaseAPI,
      action.payload
    );
    yield put(projectValidationSliceAction.setCreateTestCaseLoading(false));
    yield put(getTestCasesSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setCreateTestCaseLoading(false));
    console.log(e);
  }
}

function* updateTestCaseSaga(action: {
  type: string;
  payload: updateTestCasePayloadType;
}) {
  try {
    yield put(projectValidationSliceAction.setUpdateTestCaseLoading(true));
    let response: createAPIResponseType = yield call(
      updateTestCaseAPI,
      action.payload
    );
    yield put(projectValidationSliceAction.setUpdateTestCaseLoading(false));
    yield put(getTestCasesSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setUpdateTestCaseLoading(false));
    console.log(e);
  }
}

function* deleteTestCaseSaga(action: {
  type: string;
  payload: { id: string | number };
}) {
  try {
    yield put(projectValidationSliceAction.setDeleteTestCaseLoading(true));
    let response: createAPIResponseType = yield call(
      deleteTestCaseAPI,
      action.payload.id
    );
    yield put(projectValidationSliceAction.setDeleteTestCaseLoading(false));
    yield put(getTestCasesSagaAction());
  } catch (e) {
    yield put(projectValidationSliceAction.setDeleteTestCaseLoading(false));
    console.log(e);
  }
}

// Media Files Sagas
function* getMediaFilesSaga(action: {
  type: string;
  integrationId: string;
}): Generator<any, void, any> {
  try {
    yield put(
      projectValidationSliceAction.setMediaFilesIntegrationId(
        action.integrationId
      )
    );
    yield put(projectValidationSliceAction.setMediaFilesLoading(true));
    yield put(projectValidationSliceAction.setMediaFilesError(null));
    const response: GetMediaFilesResponse = yield call(
      getMediaFilesAPI,
      action.integrationId
    );
    if (response.success) {
      yield put(
        projectValidationSliceAction.setMediaFiles(response.data || [])
      );
    } else {
      yield put(
        projectValidationSliceAction.setMediaFilesError("Failed to fetch files")
      );
      yield put(projectValidationSliceAction.setMediaFiles([]));
    }
  } catch (e: any) {
    console.error("Error fetching media files:", e);
    yield put(
      projectValidationSliceAction.setMediaFilesError(
        e.response?.data?.message || "Failed to fetch files"
      )
    );
    yield put(projectValidationSliceAction.setMediaFiles([]));
  } finally {
    yield put(projectValidationSliceAction.setMediaFilesLoading(false));
  }
}

function* uploadMediaFilesSaga(action: {
  type: string;
  integrationId: string;
  files: File[];
  uploadedBy: number;
  createdAt?: string;
}): Generator<any, void, any> {
  try {
    yield put(projectValidationSliceAction.setMediaFilesUploading(true));
    yield put(projectValidationSliceAction.setMediaFilesError(null));
    const response: UploadMediaFilesResponse = yield call(
      uploadMediaFilesAPI,
      action.integrationId,
      action.files,
      action.uploadedBy,
      action.createdAt
    );
    if (response.success) {
      // Refresh the files list after successful upload
      yield put(getMediaFilesSagaAction(action.integrationId));
    } else {
      yield put(
        projectValidationSliceAction.setMediaFilesError(
          response.message || "Failed to upload files"
        )
      );
    }
  } catch (e: any) {
    console.error("Error uploading files:", e);
    yield put(
      projectValidationSliceAction.setMediaFilesError(
        e.response?.data?.message || "Failed to upload files"
      )
    );
  } finally {
    yield put(projectValidationSliceAction.setMediaFilesUploading(false));
  }
}

function* deleteMediaFileSaga(action: {
  type: string;
  payload: deleteMediaFilePayloadType;
}): Generator<any, void, any> {
  try {
    yield put(
      projectValidationSliceAction.setMediaFilesDeleting(action.payload.mediaId)
    );
    yield put(projectValidationSliceAction.setMediaFilesError(null));
    const response: DeleteMediaFileResponse = yield call(
      deleteMediaFileAPI,
      action.payload.mediaId
    );
    if (response.success) {
      // Get integrationId from state to refresh files
      const state: any = yield select();
      const integrationId = state.project_validation?.mediaFilesIntegrationId;
      if (integrationId) {
        yield put(getMediaFilesSagaAction(integrationId));
      }
      action.payload.setDeleteModalOpen(false);
      action.payload.setFileToDelete(null);
    } else {
      yield put(
        projectValidationSliceAction.setMediaFilesError(
          response.message || "Failed to delete file"
        )
      );
    }
  } catch (e: any) {
    console.error("Error deleting file:", e);
    yield put(
      projectValidationSliceAction.setMediaFilesError(
        e.response?.data?.message || "Failed to delete file"
      )
    );
  } finally {
    yield put(projectValidationSliceAction.setMediaFilesDeleting(null));
  }
}

function* getEnvironmentDetailsSaga(action: {
  type: string;
  payload: { integration_id: number; documentation_id?: number };
}): Generator<any, void, any> {
  try {
    yield put(projectValidationSliceAction.setEnvironmentStatusLoading(true));
    yield put(projectValidationSliceAction.setEnvironmentStatusError(null));
    const response: getEnvironmentDetailsAPIResponseType = yield call(
      getEnvironmentDetailsAPI,
      action.payload
    );
    if (response.success && response.environment) {
      yield put(
        projectValidationSliceAction.setEnvironmentDetails(response.environment)
      );
    } else {
      // Clear environment details on any unsuccessful response
      yield put(projectValidationSliceAction.setEnvironmentDetails(null));
    }
  } catch (e: any) {
    console.error("Error fetching environment details:", e);
    // On API error, clear environment details so the form is empty
    yield put(projectValidationSliceAction.setEnvironmentDetails(null));
    const message =
      e?.response?.data?.message || "Failed to fetch environment details";
    yield put(projectValidationSliceAction.setEnvironmentStatusError(message));
  } finally {
    yield put(projectValidationSliceAction.setEnvironmentStatusLoading(false));
  }
}

function* getHistoryEnvironmentDetailsSaga(action: {
  type: string;
  payload: getHistoryEnvironmentDetailsPayloadType;
}): Generator<any, void, any> {
  try {
    yield put(
      projectValidationSliceAction.setHistoryEnvironmentStatusLoading(true)
    );
    yield put(
      projectValidationSliceAction.setHistoryEnvironmentStatusError(null)
    );
    const response: getEnvironmentDetailsAPIResponseType = yield call(
      getEnvironmentDetailsAPI,
      action.payload
    );
    if (response.success && response.environment) {
      yield put(
        projectValidationSliceAction.setHistoryEnvironmentDetails(
          response.environment
        )
      );
    } else {
      yield put(projectValidationSliceAction.setHistoryEnvironmentDetails(null));
    }
  } catch (e: any) {
    console.error("Error fetching history environment details:", e);
    yield put(projectValidationSliceAction.setHistoryEnvironmentDetails(null));
    const message =
      e?.response?.data?.message ||
      "Failed to fetch history environment details";
    yield put(
      projectValidationSliceAction.setHistoryEnvironmentStatusError(message)
    );
  } finally {
    yield put(
      projectValidationSliceAction.setHistoryEnvironmentStatusLoading(false)
    );
  }
}

// Root Saga
function* projectValidationSaga() {
  yield takeLatest(
    projectValidationSagaActionTypes.callGetCategoriesSaga,
    getCategoriesSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callGetCategorySaga,
    getCategorySaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callCreateCategorySaga,
    createCategorySaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callUpdateCategorySaga,
    updateCategorySaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callDeleteCategorySaga,
    deleteCategorySaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callGetRequirementsSaga,
    getRequirementsSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callGetRequirementSaga,
    getRequirementSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callCreateRequirementSaga,
    createRequirementSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callUpdateRequirementSaga,
    updateRequirementSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callDeleteRequirementSaga,
    deleteRequirementSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callGetTestCasesSaga,
    getTestCasesSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callGetTestCaseSaga,
    getTestCaseSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callCreateTestCaseSaga,
    createTestCaseSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callUpdateTestCaseSaga,
    updateTestCaseSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callDeleteTestCaseSaga,
    deleteTestCaseSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callGetMediaFilesSaga,
    getMediaFilesSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callUploadMediaFilesSaga,
    uploadMediaFilesSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callDeleteMediaFileSaga,
    deleteMediaFileSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callGetEnvironmentDetailsSaga,
    getEnvironmentDetailsSaga
  );
  yield takeLatest(
    projectValidationSagaActionTypes.callGetHistoryEnvironmentDetailsSaga,
    getHistoryEnvironmentDetailsSaga
  );
}

export default projectValidationSaga;
export { getCategoriesSagaAction };
